// src/components/reference/ReferenceDetailModal.tsx
/**
 * 設定資料詳細モーダル
 * XorPixViewerでの画像表示 + タイトル・説明 + 削除機能
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import XorPixViewer from '../xorpix/XorPixViewer';
import { ReferenceImage } from './ReferenceGallery';

interface ReferenceDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  reference: ReferenceImage | null;
  isOwner: boolean;
  onDelete?: (modelId: string) => void;
}

const ReferenceDetailModal: React.FC<ReferenceDetailModalProps> = ({
  isOpen,
  onClose,
  reference,
  isOwner,
  onDelete,
}) => {
  const { t } = useTranslation('character');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen || !reference) return null;

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(reference.modelId);
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-end px-3 py-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            {isOwner && onDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                title={t('settings.reference.delete')}
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* コンテンツ */}
        <div className="flex-1 overflow-y-auto">
          {/* XorPixViewer */}
          <div className="bg-gray-100 dark:bg-gray-900">
            <XorPixViewer
              noise1Url={reference.noise1Url}
              noise2Url={reference.noise2Url}
              rid={reference.rid}
              width={reference.width}
              height={reference.height}
              fallbackNoise1Url={reference.fallbackNoise1Url}
              fallbackNoise2Url={reference.fallbackNoise2Url}
              fitMode="contain"
              className="w-full max-h-[50vh] object-contain"
            />
          </div>

          {/* タイトル・説明 */}
          <div className="p-5">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {reference.title}
            </h3>
            {reference.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                {reference.description}
              </p>
            )}
          </div>
        </div>

        {/* 削除確認ダイアログ */}
        {showDeleteConfirm && (
          <div
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-sm w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                {t('settings.reference.deleteConfirm')}
              </h3>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  {t('actions.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {isDeleting ? '...' : t('settings.reference.delete')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferenceDetailModal;
